<template>
  <v-dialog
    v-model="dialog"
    max-width="95vw"
    max-height="95vh"
    overlay-opacity="0.8"
    transition="dialog-transition"
    @keydown.esc="closeModal"
  >
    <v-card class="image-preview-card" elevation="24">
      <!-- Header -->
      <v-card-title class="image-preview-header pa-4">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <v-icon class="mr-3" color="primary" large>mdi-chart-scatter-plot</v-icon>
            <div>
              <h3 class="text-h6 mb-0">{{ imageTitle || 'Matrix Visualization' }}</h3>
              <p class="text-caption grey--text ma-0" v-if="imageDescription">
                {{ imageDescription }}
              </p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  @click="zoomOut"
                  :disabled="zoomLevel <= 0.5"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                >
                  <v-icon>mdi-magnify-minus</v-icon>
                </v-btn>
              </template>
              <span>Zoom Out</span>
            </v-tooltip>

            <v-chip class="mx-2" small outlined> {{ Math.round(zoomLevel * 100) }}% </v-chip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  @click="zoomIn"
                  :disabled="zoomLevel >= 3"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                >
                  <v-icon>mdi-magnify-plus</v-icon>
                </v-btn>
              </template>
              <span>Zoom In</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon @click="resetZoom" v-bind="attrs" v-on="on" class="mr-2">
                  <v-icon>mdi-fit-to-page</v-icon>
                </v-btn>
              </template>
              <span>Reset Zoom</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  @click="downloadImage"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  color="primary"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Download Image</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon @click="toggleFullscreen" v-bind="attrs" v-on="on" class="mr-2">
                  <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}</span>
            </v-tooltip>

            <v-btn icon @click="closeModal" large>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Image Container -->
      <v-card-text class="image-container pa-0" :class="{ 'fullscreen-container': isFullscreen }">
        <div
          class="image-wrapper"
          ref="imageWrapper"
          @wheel.prevent="handleWheel"
          @mousedown="startPan"
          @mousemove="handlePan"
          @mouseup="endPan"
          @mouseleave="endPan"
        >
          <v-img
            v-if="imageUrl"
            :src="imageUrl"
            :alt="imageTitle"
            class="preview-image"
            :style="imageStyle"
            contain
            @load="onImageLoad"
            @error="onImageError"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-row>
            </template>
          </v-img>

          <!-- Error State -->
          <div v-else-if="hasError" class="error-state">
            <v-icon size="64" color="error">mdi-image-broken</v-icon>
            <h3 class="mt-4">Failed to load image</h3>
            <p class="text-caption">The image could not be loaded. Please try again.</p>
            <v-btn color="primary" @click="retryLoad">Retry</v-btn>
          </div>

          <!-- Loading State -->
          <div v-else class="loading-state">
            <v-progress-circular size="64" indeterminate color="primary"></v-progress-circular>
            <h3 class="mt-4">Loading image...</h3>
          </div>
        </div>
      </v-card-text>

      <!-- Footer with metadata -->
      <v-divider></v-divider>
      <v-card-actions class="image-footer pa-4">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <v-chip class="mr-2" small color="primary" outlined>
              <v-icon left small>mdi-calendar</v-icon>
              {{ formatDate(new Date()) }}
            </v-chip>
            <v-chip v-if="fileSize" class="mr-2" small outlined>
              <v-icon left small>mdi-file</v-icon>
              {{ fileSize }}
            </v-chip>
            <v-chip v-if="imageDimensions" small outlined>
              <v-icon left small>mdi-resize</v-icon>
              {{ imageDimensions }}
            </v-chip>
          </div>

          <v-btn color="primary" @click="closeModal">
            Close
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'ImagePreviewModal',
  data() {
    return {
      dialog: false,
      imageUrl: null,
      imageTitle: null,
      imageDescription: null,
      zoomLevel: 1,
      panX: 0,
      panY: 0,
      isPanning: false,
      lastPanX: 0,
      lastPanY: 0,
      isFullscreen: false,
      hasError: false,
      fileSize: null,
      imageDimensions: null,
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `scale(${this.zoomLevel}) translate(${this.panX}px, ${this.panY}px)`,
        transformOrigin: 'center center',
        transition: this.isPanning ? 'none' : 'transform 0.2s ease',
        cursor: this.isPanning ? 'grabbing' : 'grab',
      };
    },
  },
  created() {
    EventBus.$on('show-image-preview', this.showImage);
  },
  beforeDestroy() {
    EventBus.$off('show-image-preview', this.showImage);
  },
  methods: {
    showImage(data) {
      this.imageUrl = data.url;
      this.imageTitle = data.title || 'Matrix Visualization';
      this.imageDescription = data.description || 'Generated prioritization matrix visualization';
      this.fileSize = data.fileSize || null;
      this.resetView();
      this.hasError = false;
      this.dialog = true;
    },

    closeModal() {
      this.dialog = false;
      this.resetView();
      setTimeout(() => {
        this.imageUrl = null;
        this.hasError = false;
      }, 300);
    },

    resetView() {
      this.zoomLevel = 1;
      this.panX = 0;
      this.panY = 0;
      this.isFullscreen = false;
    },

    zoomIn() {
      this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
    },

    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
    },

    resetZoom() {
      this.zoomLevel = 1;
      this.panX = 0;
      this.panY = 0;
    },

    handleWheel(event) {
      const delta = event.deltaY;
      if (delta > 0) {
        this.zoomOut();
      } else {
        this.zoomIn();
      }
    },

    startPan(event) {
      if (this.zoomLevel > 1) {
        this.isPanning = true;
        this.lastPanX = event.clientX;
        this.lastPanY = event.clientY;
      }
    },

    handlePan(event) {
      if (this.isPanning && this.zoomLevel > 1) {
        const deltaX = event.clientX - this.lastPanX;
        const deltaY = event.clientY - this.lastPanY;

        this.panX += deltaX / this.zoomLevel;
        this.panY += deltaY / this.zoomLevel;

        this.lastPanX = event.clientX;
        this.lastPanY = event.clientY;
      }
    },

    endPan() {
      this.isPanning = false;
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },

    downloadImage() {
      if (this.imageUrl) {
        const link = document.createElement('a');
        link.href = this.imageUrl;
        link.download =
          this.imageTitle?.replace(/\s+/g, '_') + '.png' || 'matrix_visualization.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        EventBus.$emit('snackbar', {
          text: 'Image downloaded successfully',
          color: 'success',
        });
      }
    },

    onImageLoad(event) {
      const img = event.target;
      this.imageDimensions = `${img.naturalWidth} × ${img.naturalHeight}`;
      this.hasError = false;
    },

    onImageError() {
      this.hasError = true;
    },

    retryLoad() {
      this.hasError = false;
      // Force reload by changing the URL slightly
      const url = new URL(this.imageUrl);
      url.searchParams.set('retry', Date.now());
      this.imageUrl = url.toString();
    },

    formatDate(date) {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
  },
};
</script>

<style scoped>
.image-preview-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.image-preview-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-bottom: 1px solid #e0e0e0;
}

.image-container {
  height: 70vh;
  max-height: 800px;
  overflow: hidden;
  position: relative;
  background: #f8f9fa;
}

.fullscreen-container {
  height: 85vh !important;
  max-height: none !important;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  user-select: none;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #666;
}

.image-footer {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.dialog-transition-enter-active,
.dialog-transition-leave-active {
  transition: all 0.3s ease;
}

.dialog-transition-enter,
.dialog-transition-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Custom scrollbar for better UX */
.image-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.image-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.image-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.image-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
